/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { never } from '../../util.type';
import ErrorLevel from '../error/error-level';
import ErrorLevelUtil from '../error/error-level-util';

/**
 * @private
 * @typedef {import('../logger/logger').Logger} Logger
 */

/**
 * Zaznamenávač výjimek pro Console API.
 * Vypisuje chyby do vývojářské konzole v prohlížeči.
 * @implements {Logger}
 */
class ConsoleLogger {
  /**
   * Zaznamená libovolnou hodnotu,
   * přičemž se hodnota úrovně chyby použije primárně z této hodnoty
   * a sekundárně z názvu metody zanamenávače chyb,
   * přes kterou byla chyba předána.
   * @param {ErrorLevel} methodLevel Úroveň chyby, která přísluší metodě,
   * která byla na zaznamenávači výjimek volána.
   * @param {any} value Libovolná hodnota k zalogování.
   * @private
   */
  capture(methodLevel, value) {
    const { console } = window;
    const valueLevel = ErrorLevelUtil.fromValue(value);
    const level = valueLevel ?? methodLevel;
    switch (level) {
      case ErrorLevel.Debug:
        return console.debug(value);
      case ErrorLevel.Error:
        return console.error(value);
      case ErrorLevel.Info:
        return console.info(value);
      case ErrorLevel.Log:
        return console.log(value);
      case ErrorLevel.Warning:
        return console.warn(value);
      default:
        never(level);
        throw new RangeError(`Unknown level "${level}".`);
    }
  }

  /** @type {Logger['debug']} */
  debug(value) {
    return this.capture(ErrorLevel.Debug, value);
  }

  /** @type {Logger['error']} */
  error(value) {
    return this.capture(ErrorLevel.Error, value);
  }

  /** @type {Logger['info']} */
  info(value) {
    return this.capture(ErrorLevel.Info, value);
  }

  /** @type {Logger['log']} */
  log(value) {
    return this.capture(ErrorLevel.Log, value);
  }

  /** @type {Logger['setInfo']} */
  setInfo(_loggerInfo) {
    // Prázdná metoda pro splnění rozhraní.
    // Do vývojářské konzole se žádné dodatečné informace nevypisují.
  }

  /** @type {Logger['warn']} */
  warn(value) {
    return this.capture(ErrorLevel.Warning, value);
  }
}

export default ConsoleLogger;
